/**
 * Calculates the total tax amount for a given base (net or gross) amount.
 *
 * @param amount The base amount:
 * 	- if isTaxIncluded = false, this is the net amount (before tax)
 *  - if isTaxIncluded = true, this is the gross amount (including tax)
 * @param taxRate The tax rate as a percentage (e.g. 19 for 19%).
 * @param isTaxIncluded Whether `amount` already includes tax (gross). Defaults to false.
 * @returns The tax portion.
 * @example
 * ```ts
 * const amount = 400;
 * const taxRate = 19;
 * const isTaxIncluded = true;
 * const totalTax = amountToTotalTax(amount, taxRate, isTaxIncluded);
 * console.log(totalTax); // Output: 63.86554621848739
 * ```
 * @example
 * ```ts
 * const amount = 400;
 * const taxRate = 19;
 * const isTaxIncluded = false;
 * const totalTax = amountToTotalTax(amount, taxRate, isTaxIncluded);
 * console.log(totalTax); // Output: 76
 * ```
 */
export function amountToTotalTax(
	amount?: number | null,
	taxRate?: number | null,
	isTaxIncluded?: boolean | null,
): number {
	// Sanitize
	const base = amount == null || isNaN(amount) ? 0 : amount;
	const rate = taxRate == null || isNaN(taxRate) ? 0 : taxRate;

	if (rate === 0 || base === 0) {
		return 0;
	}

	// Calculate tax based on whether the amount is gross or net
	return isTaxIncluded ? base * (rate / (100 + rate)) : base * (rate / 100);
}
